import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils/cn";

/** When a group has more options than this, reveal a client-side search box. */
export const SEARCH_THRESHOLD = 8;

export interface FacetOption {
  value: string;
  label: string;
}

/**
 * One collapsible checkbox facet, with client-side search for long lists.
 *
 * Shared by the three resource directories (experts / outils / aides
 * financières) so a facet added to one looks and behaves the same in the others.
 * Its copy comes from the generic `common.filters.*` namespace rather than any
 * one feature's, keeping `src/components/` free of feature imports.
 */
export function FacetGroup({
  label,
  options,
  selected,
  onToggle,
  defaultOpen = false,
}: {
  label: string;
  options: FacetOption[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const showSearch = options.length > SEARCH_THRESHOLD;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <details className="group border-b border-border/70 last:border-b-0" open={defaultOpen}>
      <summary className="flex min-h-touch cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold">
        <span>
          {label}
          {selected.length > 0 && (
            <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs font-bold tabular-nums text-primary-foreground">
              {selected.length}
            </span>
          )}
        </span>
        <ChevronDown
          className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="px-4 pb-3">
        {showSearch && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("common.filters.search")}
            aria-label={t("common.filters.searchIn", { label })}
            className="mb-2 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
          />
        )}
        <div className="max-h-56 space-y-0.5 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <p className="py-1 text-sm text-muted-foreground">
              {t("common.filters.noMatch")}
            </p>
          ) : (
            filtered.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <label
                  key={o.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 py-1 text-sm",
                    checked ? "font-semibold text-foreground" : "text-muted-foreground",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onToggle(o.value, e.target.checked)}
                    className="size-4 shrink-0 rounded border-input text-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <span className="min-w-0 flex-1">{o.label}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}
